import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'https://app.qlicxo.com/api/v1';

export default function DaySelect() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState([]);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Override viewport for mobile responsiveness on this public page
  useEffect(() => {
    const viewport = document.querySelector('meta[name="viewport"]');
    const original = viewport?.getAttribute('content');
    if (viewport) {
      viewport.setAttribute('content', 'width=device-width, initial-scale=1, maximum-scale=5');
    }
    return () => {
      if (viewport && original) viewport.setAttribute('content', original);
    };
  }, []);

  useEffect(() => {
    axios.get(`${API_BASE}/day-select/${token}`)
      .then(res => {
        const d = res.data.data;
        setData(d);
        if (d.status === 'submitted' || d.status === 'agency_filled') {
          setSelected(d.selections || []);
          setNote(d.client_note || '');
          setSubmitted(true);
        }
      })
      .catch(() => setError('This link is invalid or has expired.'))
      .finally(() => setLoading(false));
  }, [token]);

  const toggleDay = (day) => {
    if (submitted) return;
    const exists = selected.find(s => s.date === day.date);
    if (exists) {
      setSelected(selected.filter(s => s.date !== day.date));
    } else {
      if (selected.length >= data.allowance) return;
      setSelected([...selected, day]);
    }
  };

  const handleSubmit = async () => {
    if (selected.length === 0) return;
    setSubmitting(true);
    try {
      await axios.post(`${API_BASE}/day-select/${token}`, {
        selections: selected,
        client_note: note || null,
      });
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <div className="ds-page">
      <style>{responsiveCSS}</style>
      <div className="ds-card"><p className="ds-loading">Loading...</p></div>
    </div>
  );

  if (error && !data) return (
    <div className="ds-page">
      <style>{responsiveCSS}</style>
      <div className="ds-card">
        <div className="ds-brand"><div className="ds-logo">Q</div><span className="ds-brand-name">Qlicxo</span></div>
        <p className="ds-error">{error}</p>
      </div>
    </div>
  );

  const monthLabel = data.month ? new Date(data.month + '-01').toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : data.month;

  // Group days by category
  const grouped = {};
  (data.days || []).forEach(d => {
    const cat = d.cat || 'Other';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(d);
  });

  return (
    <div className="ds-page">
      <style>{responsiveCSS}</style>
      <div className="ds-card">
        {/* Header */}
        <div className="ds-brand">
          <div className="ds-logo">Q</div>
          <div>
            <div className="ds-brand-name">Qlicxo</div>
            <div className="ds-tagline">Content Day Selection</div>
          </div>
        </div>

        {submitted && (
          <div className="ds-submitted-banner">
            ✓ {data.status === 'agency_filled' ? 'Selections updated by agency' : 'Your selections have been submitted'}
          </div>
        )}

        <h2 className="ds-title">
          {submitted ? 'Your Selections' : 'Select Your Special Days'}
        </h2>
        <p className="ds-subtitle">
          Hi <strong>{data.client_name}</strong> — {submitted
            ? `here are your selected days for ${monthLabel}.`
            : `please choose up to ${data.allowance} special days for ${monthLabel} that you'd like us to create content for.`
          }
        </p>

        {/* Selection counter */}
        {!submitted && (
          <div className="ds-counter">
            <span className="ds-counter-num" style={{ color: selected.length >= data.allowance ? '#ef4444' : '#3b82f6' }}>
              {selected.length}
            </span>
            <span className="ds-counter-label"> / {data.allowance} selected</span>
          </div>
        )}

        {/* Days grouped by category */}
        {Object.entries(grouped).map(([cat, days]) => (
          <div key={cat} className="ds-cat-group">
            <div className="ds-cat-label">{cat}</div>
            {days.map(day => {
              const isSelected = selected.some(sl => sl.date === day.date);
              const isDisabled = !isSelected && selected.length >= data.allowance && !submitted;
              return (
                <div
                  key={day.date}
                  onClick={() => !isDisabled && toggleDay(day)}
                  className={`ds-day-row ${isSelected ? 'ds-day-selected' : ''} ${isDisabled ? 'ds-day-disabled' : ''} ${submitted ? 'ds-day-submitted' : ''}`}
                >
                  <div className="ds-day-check">
                    {isSelected ? (
                      <div className="ds-checkbox-checked">✓</div>
                    ) : (
                      <div className="ds-checkbox-empty" />
                    )}
                  </div>
                  <div className="ds-day-info">
                    <div className="ds-day-name">{day.name}</div>
                    <div className="ds-day-date">{day.date}</div>
                  </div>
                </div>
              );
            })}
          </div>
        ))}

        {/* Note */}
        {submitted ? (
          note && (
            <div className="ds-note-display">
              <div className="ds-note-label">Your Note</div>
              <p className="ds-note-text">{note}</p>
            </div>
          )
        ) : (
          <div style={{ marginTop: '20px' }}>
            <label className="ds-note-label">Add a note (optional)</label>
            <textarea
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder="Any preferences or instructions for the content team..."
              maxLength={1000}
              className="ds-textarea"
              rows={3}
            />
          </div>
        )}

        {/* Error */}
        {error && <p className="ds-error">{error}</p>}

        {/* Submit */}
        {!submitted && (
          <button
            onClick={handleSubmit}
            disabled={submitting || selected.length === 0}
            className="ds-submit-btn"
            style={{ opacity: (submitting || selected.length === 0) ? 0.5 : 1 }}
          >
            {submitting ? 'Submitting...' : `Submit ${selected.length} Selection${selected.length !== 1 ? 's' : ''}`}
          </button>
        )}

        <p className="ds-footer">Powered by Qlicxo WorkOps</p>
      </div>
    </div>
  );
}

// ── Responsive CSS ──────────────────────────────────────────────────────

const responsiveCSS = `
  .ds-page {
    min-height: 100vh;
    background: linear-gradient(135deg, #f0f4ff 0%, #e8ecf8 100%);
    display: flex;
    align-items: flex-start;
    justify-content: center;
    padding: 40px 16px;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
    box-sizing: border-box;
  }
  .ds-card {
    background: #fff;
    border-radius: 16px;
    box-shadow: 0 4px 24px rgba(0,0,0,0.08);
    padding: 32px;
    width: 100%;
    max-width: 560px;
    box-sizing: border-box;
  }
  .ds-brand {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 24px;
  }
  .ds-logo {
    width: 40px; height: 40px; border-radius: 10px;
    background: linear-gradient(135deg, #3b82f6, #6366f1);
    color: #fff; display: flex; align-items: center; justify-content: center;
    font-weight: 700; font-size: 20px; flex-shrink: 0;
  }
  .ds-brand-name { font-weight: 700; font-size: 18px; color: #1e293b; }
  .ds-tagline { font-size: 12px; color: #64748b; }
  .ds-submitted-banner {
    background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 8px;
    padding: 12px 16px; color: #166534; font-weight: 600; font-size: 14px;
    margin-bottom: 16px;
  }
  .ds-title { font-size: 22px; font-weight: 700; color: #1e293b; margin: 0 0 8px; }
  .ds-subtitle { font-size: 14px; color: #475569; line-height: 1.5; margin: 0 0 20px; }
  .ds-counter { margin-bottom: 16px; font-size: 14px; }
  .ds-counter-num { font-weight: 700; font-size: 20px; }
  .ds-counter-label { color: #64748b; }
  .ds-cat-group { margin-bottom: 16px; }
  .ds-cat-label {
    font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em;
    color: #94a3b8; margin-bottom: 6px; padding-left: 4px;
  }
  .ds-day-row {
    display: flex; align-items: center; gap: 12px;
    padding: 14px 16px; border-radius: 10px; margin-bottom: 6px;
    border: 1.5px solid #e2e8f0; cursor: pointer;
    transition: all 0.15s ease;
    -webkit-tap-highlight-color: transparent;
  }
  .ds-day-row:active { transform: scale(0.98); }
  .ds-day-selected { border-color: #3b82f6; background: #eff6ff; }
  .ds-day-disabled { opacity: 0.45; cursor: not-allowed; }
  .ds-day-submitted { cursor: default; }
  .ds-day-check { flex-shrink: 0; }
  .ds-checkbox-checked {
    width: 24px; height: 24px; border-radius: 6px;
    background: #3b82f6; color: #fff; display: flex;
    align-items: center; justify-content: center; font-size: 14px; font-weight: 700;
  }
  .ds-checkbox-empty {
    width: 24px; height: 24px; border-radius: 6px;
    border: 2px solid #cbd5e1;
  }
  .ds-day-info { flex: 1; min-width: 0; }
  .ds-day-name { font-size: 15px; font-weight: 600; color: #1e293b; }
  .ds-day-date { font-size: 13px; color: #64748b; margin-top: 2px; }
  .ds-note-display {
    margin-top: 20px; background: #f8fafc; border-radius: 8px; padding: 14px 16px;
  }
  .ds-note-label {
    font-size: 13px; font-weight: 600; color: #475569; margin-bottom: 6px; display: block;
  }
  .ds-note-text { font-size: 14px; color: #334155; margin: 0; line-height: 1.5; }
  .ds-textarea {
    width: 100%; padding: 12px 14px; border-radius: 8px;
    border: 1.5px solid #e2e8f0; font-size: 16px; font-family: inherit;
    resize: vertical; outline: none; box-sizing: border-box;
    -webkit-appearance: none;
  }
  .ds-textarea:focus { border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59,130,246,0.1); }
  .ds-error { color: #ef4444; font-size: 14px; margin-top: 12px; }
  .ds-loading { text-align: center; color: #64748b; font-size: 16px; }
  .ds-submit-btn {
    width: 100%; padding: 16px; border-radius: 12px; border: none;
    background: linear-gradient(135deg, #3b82f6, #6366f1);
    color: #fff; font-size: 16px; font-weight: 700; cursor: pointer;
    margin-top: 24px; -webkit-appearance: none;
    transition: transform 0.1s ease;
  }
  .ds-submit-btn:active { transform: scale(0.98); }
  .ds-footer {
    text-align: center; font-size: 11px; color: #94a3b8;
    margin-top: 24px; padding-top: 16px; border-top: 1px solid #f1f5f9;
  }

  /* ── Mobile (≤480px) ───────────────────────────────────── */
  @media (max-width: 480px) {
    .ds-page {
      padding: 0;
      align-items: stretch;
      background: #fff;
    }
    .ds-card {
      border-radius: 0;
      box-shadow: none;
      padding: 24px 20px;
      max-width: 100%;
    }
    .ds-title { font-size: 20px; }
    .ds-subtitle { font-size: 14px; }
    .ds-day-row { padding: 14px 14px; gap: 14px; border-radius: 12px; margin-bottom: 8px; }
    .ds-day-name { font-size: 15px; }
    .ds-day-date { font-size: 13px; }
    .ds-checkbox-checked, .ds-checkbox-empty { width: 26px; height: 26px; }
    .ds-checkbox-checked { font-size: 15px; }
    .ds-submit-btn { padding: 18px; font-size: 17px; border-radius: 14px; }
    .ds-textarea { font-size: 16px; padding: 14px; }
  }

  /* ── Tablet (481px – 768px) ────────────────────────────── */
  @media (min-width: 481px) and (max-width: 768px) {
    .ds-page { padding: 24px 16px; }
    .ds-card { padding: 28px 24px; }
  }
`;
